import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';
import { DateTime } from 'luxon';

import {
    Container, Typography, Divider, Grid, Box, FormControl, FormLabel, Button, CircularProgress,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material'

import { useTheme } from '@mui/material/styles'

import { StaticDatePicker } from '@mui/x-date-pickers';

import CustomChip from '../../utils/components/CustomChip';

import useWindowDimensions from '../../utils/functions/useWindowDimensions';

import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from "recharts";

import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
} from "recharts";

import { formatMoney } from '../../utils/functions/Utils';

const ReporteVentas = () => {

    const theme = useTheme()

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [fechaInicio, setFechaInicio] = useState(DateTime.now())
    const [fechaFin, setFechaFin] = useState(DateTime.now())
    const [data, setData] = useState([{}]);
    const [weekReport, setWeekReport] = useState({ report: { nombre: '' }, categorias: [{ nombre: '', cantidad: 0 }] });
    const [timeReport, setTimeReport] = useState({ report: { nombre: '' }, categorias: [{ nombre: '', cantidad: 0 }] });
    const [loading, setLoading] = useState(true);

    const { width } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin', 'Ayudante'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Reporte de Ventas';
    }, [auth])

    const getData = async (inicio, fin) => {
        try {
            setLoading(true);
            const res = await axios.post('/api/orders/salesReport', { start: inicio, end: fin })
            if (res.status === 200) {
                setData(res.data.report.map(x => { return x.report }));
                setWeekReport(res.data.weekReport);
                setTimeReport(res.data.timeReport);
                setLoading(false);
            }

        } catch (err) {
            console.log(err)
            setLoading(false);
        }
    }

    useEffect(() => {
        const fin = DateTime.now();
        const inicio = fin.minus({ months: 1 });
        setFechaInicio(inicio);
        setFechaFin(fin);
        getData(inicio, fin);
    }, [])

    const handleButton = async () => {
        getData(fechaInicio, fechaFin);
    }

    return (
        <Container maxWidth='xxl' style={{ marginTop: '10px' }}>
            {loading ?
                <Box style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '128px' }}>
                    <CircularProgress size='5em' color='primary' />
                </Box> :
                <>
                    <Container maxWidth='xl'>
                        <Typography variant='h4' style={{ fontWeight: 700, textAlign: 'center' }}>Reporte de Ventas</Typography>

                        <Button size='small' variant='outlined' onClick={() => navigate('/informes')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>

                        <Box style={{ display: 'flex', margin: '12px 0px', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography variant='h6' style={{ fontWeight: 500 }}>Reporte de la semana &nbsp;</Typography>
                            <Typography variant='h6' style={{ fontWeight: 500 }} color='comp.spColor1'>({weekReport.report.nombre})</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant='h5' style={{ fontWeight: 500 }}>Ventas completas: </Typography>
                                    <Typography variant='h3' style={{ fontWeight: 700, margin: '0px 8px' }} color='comp.spColor2'>{weekReport.report.orders}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant='h5' style={{ fontWeight: 500 }}>Ventas de la semana (MXN)</Typography>
                                    <Typography variant='h3' style={{ fontWeight: 700, margin: '0px 8px' }} color='comp.spColor2'>$ {formatMoney(weekReport.report.total)}</Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RadarChart
                                cx={width > 600 ? 300 : width / 2}
                                cy={250}
                                outerRadius={width > 600 ? 150 : width / 4 - 20}
                                width={width > 600 ? 600 : width - 20}
                                height={500}
                                data={weekReport.categorias}
                            >
                                <PolarGrid />
                                <PolarAngleAxis dataKey="nombre" />
                                <PolarRadiusAxis />
                                <Radar
                                    name="Cantidad"
                                    dataKey="cantidad"
                                    stroke={theme.palette.info.main}
                                    fill={theme.palette.secondary.main}
                                    fillOpacity={0.6}
                                />
                                <Tooltip
                                    labelStyle={{ fontWeight: 500, padding: '8px' }}
                                    itemStyle={{ padding: '8px' }}
                                    contentStyle={{ background: `${theme.palette.background.default}`, borderRadius: '20px' }}
                                />
                                <Legend />
                            </RadarChart>
                        </Box>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow hover sx={{ backgroundColor: 'comp.tableHeaderBg' }}>
                                        {
                                            weekReport.categorias.map((cat, i) => (
                                                <TableCell key={i} align='center' sx={{ fontWeight: '400', fontSize: '1em' }}>{cat.nombre}</TableCell>
                                            ))
                                        }
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow hover sx={{ backgroundColor: 'comp.tableBg' }}>
                                        {weekReport.categorias.map((cat, i) => (
                                            <TableCell key={i} size='small' sx={{ fontWeight: '400', fontSize: '1.4em' }} align='center'>{cat.cantidad}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Divider className='my-5' textAlign="left">
                            <CustomChip variant='outlined' estatus='AUTORIZADO' label='Periodo de ventas' className='text-bold' />
                        </Divider>

                        <Typography variant='h6' style={{ fontWeight: 500 }}>Selecciona el periodo de ventas</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                    <FormLabel component="legend">Fecha de Inicio</FormLabel>
                                    <StaticDatePicker
                                        label="Fecha de Inicio"
                                        format="dd/MM/yyyy"
                                        value={fechaInicio}
                                        orientation='landscape'
                                        onChange={(newValue) => setFechaInicio(newValue)}
                                        slotProps={{ textField: { variant: 'outlined' } }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                    <FormLabel component="legend">Fecha final</FormLabel>
                                    <StaticDatePicker
                                        label="Fecha final"
                                        format="dd/MM/yyyy"
                                        value={fechaFin}
                                        orientation='landscape'
                                        helperText="Fecha final"
                                        onChange={(newValue) => setFechaFin(newValue)}
                                        slotProps={{ textField: { variant: 'outlined' } }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                                    <Button variant='contained' color='primary' size='large' onClick={() => handleButton()}>Generar Reporte</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>

                    <Box style={{ display: 'flex', margin: '12px 0px', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant='h6' style={{ fontWeight: 500 }}>Reporte de venta &nbsp;</Typography>
                        <Typography variant='h6' style={{ fontWeight: 500 }} color='comp.spColor1'>({timeReport.report ? timeReport.report.nombre : ''})</Typography>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Typography variant='h5' style={{ fontWeight: 500 }}>Ventas completas: </Typography>
                                <Typography variant='h3' style={{ fontWeight: 700, margin: '0px 8px' }} color='comp.spColor2'>{timeReport.report ? timeReport.report.orders : ''}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Typography variant='h5' style={{ fontWeight: 500 }}>Ventas del periodo (MXN)</Typography>
                                <Typography variant='h3' style={{ fontWeight: 700, margin: '0px 8px' }} color='comp.spColor2'>$ {formatMoney(timeReport.report ? timeReport.report.total : '')}</Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <br />

                    <Container maxWidth='xxl'>
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                            <ComposedChart
                                width={width > 600 ? width - 100 : width - 20}
                                height={400}
                                data={data}
                                margin={{
                                    top: 20,
                                    right: 20,
                                    bottom: 20,
                                    left: 20
                                }}
                            >
                                <CartesianGrid stroke={theme.palette.comp.gray} />
                                <XAxis dataKey="nombre" scale="band" />
                                <YAxis
                                    label={{
                                        value: 'Ventas (MXN)',
                                        style: { textAnchor: 'middle', fontWeight: 700 },
                                        angle: -90,
                                        position: 'left',
                                        offset: 8,
                                    }}
                                    yAxisId="left"

                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    label={{
                                        value: 'Ventas Completas',
                                        style: { textAnchor: 'middle', fontWeight: 700 },
                                        angle: -90,
                                        position: 'left',
                                        offset: -52,
                                    }}
                                />
                                <Tooltip
                                    labelStyle={{ fontWeight: 500, padding: '8px' }}
                                    itemStyle={{ padding: '8px' }}
                                    contentStyle={{ background: `${theme.palette.background.default}`, borderRadius: '20px' }}
                                />
                                <Legend />
                                <Bar yAxisId="left" dataKey="total" barSize={30} fill={theme.palette.secondary.main} />
                                <Line yAxisId="right" type="monotone" dataKey="orders" stroke={theme.palette.info.main} />
                            </ComposedChart>
                        </Box>

                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RadarChart
                                cx={width > 600 ? 300 : width / 2}
                                cy={250}
                                outerRadius={width > 600 ? 150 : width / 4 - 20}
                                width={width > 600 ? 600 : width - 20}
                                height={500}
                                data={timeReport.categorias}
                            >
                                <PolarGrid />
                                <PolarAngleAxis dataKey="nombre" />
                                <PolarRadiusAxis />
                                <Radar
                                    name="Cantidad"
                                    dataKey="cantidad"
                                    stroke={theme.palette.info.main}
                                    fill={theme.palette.secondary.main}
                                    fillOpacity={0.6}
                                />
                                <Tooltip
                                    labelStyle={{ fontWeight: 500, padding: '8px' }}
                                    itemStyle={{ padding: '8px' }}
                                    contentStyle={{ background: `${theme.palette.background.default}`, borderRadius: '20px' }}
                                />
                                <Legend />
                            </RadarChart>
                        </Box>

                        <Container maxWidth='xl'>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow hover sx={{ backgroundColor: 'comp.tableHeaderBg' }}>
                                            {
                                                weekReport.categorias.map((cat, i) => (
                                                    <TableCell key={i} align='center' sx={{ fontWeight: '400', fontSize: '1em' }}>{cat.nombre}</TableCell>
                                                ))
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow hover sx={{ backgroundColor: 'comp.tableBg' }}>
                                            {timeReport.categorias.map((cat, i) => (
                                                <TableCell key={i + 100} size='small' sx={{ fontWeight: '400', fontSize: '1.4em' }} align='center'>{cat.cantidad}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Container>

                        <br />
                        <br />

                    </Container>
                </>
            }

        </Container>
    )
}

export default ReporteVentas